import React, { useEffect } from 'react';
import {
  createBrowserRouter,
  RouterProvider,
  Navigate
} from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import NotificationsProvider from './contexts/NotificationsContext';
import { DarkModeProvider } from './contexts/DarkModeContext';
import { UploadPreferencesProvider } from './contexts/UploadPreferencesContext';
import ProtectedRoute from './components/auth/ProtectedRoute';
import extensionErrorHandler from './utils/extensionErrorHandler';

// Layout components
import MainLayout from './components/layouts/MainLayout';
import AdminLayout from './components/layouts/AdminLayout';

// Auth pages
import Login from './pages/auth/Login';
import Register from './pages/auth/Register';
import ForgotPassword from './pages/auth/ForgotPassword';

// Dashboard pages
import Dashboard from './pages/Dashboard';

// Admin pages
import AdminDashboard from './pages/admin/AdminDashboard';
import UsersManagement from './pages/admin/UsersManagement';
import OrganizationsManagement from './pages/admin/OrganizationsManagement';
import AdminAddUser from './pages/admin/AdminAddUser';
import AdminEditUser from './pages/admin/AdminEditUser';
import LlmIntegration from './pages/admin/LlmIntegration';
import SystemSettings from './pages/admin/SystemSettings';
import AnalyticsDashboard from './pages/admin/AnalyticsDashboard';
import LlmQueryPage from './pages/admin/research/LlmQueryPage';
import LegalDatabaseSearchPage from './pages/admin/research/LegalDatabaseSearchPage';
import ComprehensiveResearchPage from './pages/admin/research/ComprehensiveResearchPage';
import CaseRecommendationsPage from './pages/admin/research/CaseRecommendationsPage';
import ApiUsageMetricsPage from './pages/admin/analytics/ApiUsageMetricsPage'; // Import for API Usage Metrics
import LlmUsageDashboard from './pages/admin/analytics/LlmUsageDashboard';

// Client management
import ClientsList from './pages/clients/ClientsList';
import ClientDetails from './pages/clients/ClientDetails';
import NewClient from './pages/clients/NewClient';
import EditClient from './pages/clients/EditClient';

// Case management
import CasesList from './pages/cases/CasesList';
import CaseDetails from './pages/cases/CaseDetails';
import NewCase from './pages/cases/NewCase';
import EditCase from './pages/cases/EditCase';

// Legal pages
import TermsOfService from './pages/legal/TermsOfService';
import PrivacyPolicy from './pages/legal/PrivacyPolicy';

// Test components
import LoginTest from './components/LoginTest';

// Document management
import DocumentsList from './pages/documents/DocumentsList';
import DocumentDetails from './pages/documents/DocumentDetails';
import UploadDocument from './pages/documents/UploadDocument';
import EditDocument from './pages/documents/EditDocument';

// Diary page (replacing Calendar)
import Diary from './pages/Diary';

// Google API Usage / OAuth scopes transparency page
import GoogleAPIUsage from './pages/legal/GoogleAPIUsage';

// Billing pages
import TimeEntries from './pages/billing/TimeEntries';
import Invoices from './pages/billing/Invoices';
import CreateInvoice from './pages/billing/CreateInvoice';
import InvoiceDetail from './pages/billing/InvoiceDetail';
import BillingDashboard from './pages/billing/BillingDashboard';
import BillingReports from './pages/billing/BillingReports';
import ExpensesList from './pages/billing/ExpensesList';

// Research pages
import ResearchDashboard from './pages/research/ResearchDashboard';
import CaseResearchDashboard from './pages/research/CaseResearchDashboard';

// Tasks page
import Tasks from './pages/Tasks';

// User profile
import UserProfile from './pages/profile/UserProfile';
import UserSettings from './pages/profile/UserSettings';

// Team / Organization management
import TeamManagement from './pages/organization/TeamManagement';

// Trust Account Management
import { TrustDashboard, TrustAccounts, TrustAccountDetail, TrustTransactions, TrustReports } from './pages/trust';

// Conflict Check
import { ConflictCheckDashboard, NewConflictCheck, ConflictCheckHistory } from './pages/conflicts';

// Client Portal
import { PortalManagement, PortalMessages, PortalUploads } from './pages/portal';

// AI Contract Analyzer
import { ContractAnalyzerDashboard, NewContractAnalysis, ContractAnalysisDetail } from './pages/contracts';

// Document Generator
import { DocumentGeneratorDashboard, CreateDocument } from './pages/generator';

// E-Signature
import { ESignatureDashboard, NewSignatureRequest } from './pages/esignature';

// Court Filing
import { CourtFilingDashboard } from './pages/filings';

// Conveyancing
import { ConveyancingDashboard, ConveyancingMatters, ConveyancingMatterDetail } from './pages/conveyancing';

// Estate Management
import { EstateDashboard, EstatesList, EstateDetail } from './pages/estates';

// Business Intelligence
import { BusinessIntelDashboard } from './pages/analytics';

// Legal Library (platform-wide, all roles)
import LibraryHome from './pages/library/LibraryHome';
import LibraryDocumentViewer from './pages/library/LibraryDocumentViewer';
import LibraryDocuments from './pages/admin/LibraryDocuments';

// Student (Academy) experience
import StudyHome from './pages/student/StudyHome';
import StudyModule from './pages/student/StudyModule';
import MockTrialLobby from './pages/student/MockTrialLobby';
import MockTrialSession from './pages/student/MockTrialSession';
import ProgressView from './pages/student/ProgressView';
import StudentProfile from './pages/student/StudentProfile';

function App() {
  // Define router without any hooks that rely on context
  const router = createBrowserRouter([
    // Public routes that don't require authentication
    {
      path: "/login",
      element: <Login />
    },
    {
      path: "/register",
      element: <Register />
    },
    {
      path: "/forgot-password",
      element: <ForgotPassword />
    },
    {
      path: "/terms",
      element: <TermsOfService />
    },
    {
      path: "/privacy",
      element: <PrivacyPolicy />
    },
    {
      path: "/test-login",
      element: <LoginTest />
    },

    // Protected routes
    {
      path: "/",
      element: <ProtectedRoute><MainLayout /></ProtectedRoute>,
      children: [
        { index: true, element: <Dashboard /> },
        { path: "clients", element: <ClientsList /> },
        { path: "clients/new", element: <NewClient /> },
        { path: "clients/:id", element: <ClientDetails /> },
        { path: "clients/:id/edit", element: <EditClient /> },
        { path: "cases", element: <CasesList /> },
        { path: "cases/new", element: <NewCase /> },
        { path: "cases/:id", element: <CaseDetails /> },
        { path: "cases/:id/edit", element: <EditCase /> },
        { path: "documents", element: <DocumentsList /> },
        { path: "documents/upload", element: <UploadDocument /> },
        { path: "documents/:id", element: <DocumentDetails /> },
        { path: "documents/:id/edit", element: <EditDocument /> },
        { path: "diary", element: <Diary /> },
        { path: "google-api-usage", element: <GoogleAPIUsage /> },

        // Legal Library — open to every authenticated role.
        { path: "library", element: <LibraryHome /> },
        { path: "library/:docId", element: <LibraryDocumentViewer /> },

        { path: "billing", element: <BillingDashboard /> },
        { path: "billing/time-entries", element: <TimeEntries /> },
        { path: "billing/expenses", element: <ExpensesList /> },
        { path: "billing/invoices", element: <Invoices /> },
        { path: "billing/invoices/new", element: <CreateInvoice /> },
        { path: "billing/invoices/:id", element: <InvoiceDetail /> },
        { path: "billing/reports", element: <BillingReports /> },

        { path: "research", element: <ResearchDashboard /> },
        { path: "research/case/:caseId", element: <CaseResearchDashboard /> },
        { path: "analytics", element: <AnalyticsDashboard /> },
        { path: "tasks", element: <Tasks /> },
        { path: "profile", element: <UserProfile /> },
        { path: "settings", element: <UserSettings /> },
        { path: "team", element: <TeamManagement /> },

        // Trust Account Management routes
        { path: "trust", element: <TrustDashboard /> },
        { path: "trust/accounts", element: <TrustAccounts /> },
        { path: "trust/accounts/new", element: <TrustAccounts /> },
        { path: "trust/accounts/:id", element: <TrustAccountDetail /> },
        { path: "trust/transactions", element: <TrustTransactions /> },
        { path: "trust/transactions/new", element: <TrustTransactions /> },
        { path: "trust/reconciliations", element: <TrustReports /> },
        { path: "trust/reconciliations/new", element: <TrustReports /> },

        // Conflict Check routes
        { path: "conflicts", element: <ConflictCheckDashboard /> },
        { path: "conflicts/new", element: <NewConflictCheck /> },
        { path: "conflicts/history", element: <ConflictCheckHistory /> },
        { path: "conflicts/:id", element: <ConflictCheckHistory /> },

        // Client Portal routes
        { path: "portal", element: <PortalManagement /> },
        { path: "portal/messages", element: <PortalMessages /> },
        { path: "portal/uploads", element: <PortalUploads /> },
        { path: "portal/clients/:id", element: <PortalManagement /> },

        // AI Contract Analyzer routes
        { path: "contracts", element: <ContractAnalyzerDashboard /> },
        { path: "contracts/analyze", element: <NewContractAnalysis /> },
        { path: "contracts/history", element: <ContractAnalyzerDashboard /> },
        { path: "contracts/:id", element: <ContractAnalysisDetail /> },

        // Document Generator routes
        { path: "generator", element: <DocumentGeneratorDashboard /> },
        { path: "generator/create", element: <CreateDocument /> },
        { path: "generator/templates", element: <DocumentGeneratorDashboard /> },
        { path: "generator/documents", element: <DocumentGeneratorDashboard /> },
        { path: "generator/documents/:id", element: <DocumentGeneratorDashboard /> },

        // E-Signature routes
        { path: "esignature", element: <ESignatureDashboard /> },
        { path: "esignature/new", element: <NewSignatureRequest /> },
        { path: "esignature/requests", element: <ESignatureDashboard /> },
        { path: "esignature/requests/:id", element: <ESignatureDashboard /> },
        { path: "esignature/templates", element: <ESignatureDashboard /> },

        // Court Filing routes
        { path: "filings", element: <CourtFilingDashboard /> },
        { path: "filings/new", element: <CourtFilingDashboard /> },
        { path: "filings/all", element: <CourtFilingDashboard /> },
        { path: "filings/deadlines", element: <CourtFilingDashboard /> },
        { path: "filings/courts", element: <CourtFilingDashboard /> },
        { path: "filings/:id", element: <CourtFilingDashboard /> },

        // Conveyancing routes
        { path: "conveyancing", element: <ConveyancingDashboard /> },
        { path: "conveyancing/matters", element: <ConveyancingMatters /> },
        { path: "conveyancing/matters/new", element: <ConveyancingMatters /> },
        { path: "conveyancing/matters/:id", element: <ConveyancingMatterDetail /> },

        // Estate Management routes
        { path: "estates", element: <EstateDashboard /> },
        { path: "estates/list", element: <EstatesList /> },
        { path: "estates/new", element: <EstatesList /> },
        { path: "estates/:id", element: <EstateDetail /> },

        // Business Intelligence routes
        { path: "analytics", element: <BusinessIntelDashboard /> },
        { path: "analytics/reports", element: <BusinessIntelDashboard /> },
        { path: "analytics/productivity", element: <BusinessIntelDashboard /> },
        { path: "analytics/revenue", element: <BusinessIntelDashboard /> },

        // Student (Academy) routes — only students (or superusers) may reach them.
        { path: "student/study", element: <ProtectedRoute studentOnly={true}><StudyHome /></ProtectedRoute> },
        { path: "student/study/:topicId", element: <ProtectedRoute studentOnly={true}><StudyModule /></ProtectedRoute> },
        { path: "student/mock-trials", element: <ProtectedRoute studentOnly={true}><MockTrialLobby /></ProtectedRoute> },
        { path: "student/mock-trials/:trialId", element: <ProtectedRoute studentOnly={true}><MockTrialSession /></ProtectedRoute> },
        { path: "student/progress", element: <ProtectedRoute studentOnly={true}><ProgressView /></ProtectedRoute> },
        { path: "student/profile", element: <ProtectedRoute studentOnly={true}><StudentProfile /></ProtectedRoute> },
      ],
    },

    // Admin routes
    {
      path: "/admin",
      element: <ProtectedRoute adminOnly={true}><AdminLayout /></ProtectedRoute>,
      children: [
        { index: true, element: <AdminDashboard /> },
        { path: "users", element: <UsersManagement /> },
        {
          path: "organizations",
          element: (
            <ProtectedRoute superAdminOnly={true}>
              <OrganizationsManagement />
            </ProtectedRoute>
          ),
        },
        { path: "users/add", element: <AdminAddUser /> },
        { path: "users/:id/edit", element: <AdminEditUser /> },
        { path: "llm-integration", element: <LlmIntegration /> },
        { path: "library", element: <LibraryDocuments /> },
        { path: "settings", element: <SystemSettings /> },
        { path: "analytics", element: <AnalyticsDashboard /> },
        { path: "research/llm-query", element: <LlmQueryPage /> },
        { path: "research/legal-db-search", element: <LegalDatabaseSearchPage /> },
        { path: "research/comprehensive-research", element: <ComprehensiveResearchPage /> },
        { path: "research/case-recommendations", element: <CaseRecommendationsPage /> },
        { path: "analytics/api-usage", element: <ApiUsageMetricsPage /> }, // Legacy route
        { path: "analytics/llm-usage", element: <LlmUsageDashboard /> },
      ],
    },

    // Fallback route
    {
      path: "*",
      element: <Navigate to="/login" replace />
    }
  ], {
    future: {
      v7_startTransition: true,
      v7_relativeSplatPath: true,
    },
  });

  // Initialize browser extension error handler
  useEffect(() => {
    // Enable extension error suppression
    extensionErrorHandler.enable();

    // Show diagnostic in development mode
    if (process.env.NODE_ENV === 'development') {
      setTimeout(() => {
        extensionErrorHandler.showDiagnostic();
      }, 5000); // Show after 5 seconds to capture initial errors
    }

    return () => {
      // Cleanup on unmount
      extensionErrorHandler.disable();
    };
  }, []);

  // Wrap the RouterProvider with all required providers
  return (
    <AuthProvider>
      <DarkModeProvider>
        <NotificationsProvider>
          <UploadPreferencesProvider>
            <RouterProvider router={router} />
          </UploadPreferencesProvider>
        </NotificationsProvider>
      </DarkModeProvider>
    </AuthProvider>
  );
}

export default App;